import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { Button } from "design-system/atoms/Button"
import { cn } from "design-system/utils"
import { ArrowDown, Signal } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { GetSignalsResponseDataSchema } from "schema"
import { z } from "zod"
import { MAX_VISIBLE_GROUPS } from "."
import { SignalCard } from "./SignalCard"
import { SignalGroupItem } from "./SignalGroupItem"

type SignalListItem = z.infer<typeof GetSignalsResponseDataSchema>[number]

export type SignalGroup = {
  groupKey: string
  impact: "Positive" | "Negative"
  signals: SignalListItem[]
}

type Props = {
  groups: SignalGroup[]
  sliceCount: number
  isLoading: boolean
  showViewMore: boolean
  onViewMore: () => void
  viewMoreOpen: boolean
}

export const SignalsList = ({
  groups,
  sliceCount,
  isLoading,
  showViewMore,
  onViewMore,
  viewMoreOpen,
}: Props) => {
  const { t } = useTranslation()
  const [expandedGroups, setExpandedGroups] = useState<string[]>([])

  const noData = !groups || groups.length === 0
  const initialGroups = groups.slice(0, sliceCount)
  const additionalGroups = groups.slice(sliceCount)
  const viewMoreValue = "view-more-groups"
  const isSingleItem = initialGroups.length === 1

  useEffect(() => {
    if (viewMoreOpen) {
      setExpandedGroups((prev) => (prev.includes(viewMoreValue) ? prev : [...prev, viewMoreValue]))
    } else {
      setExpandedGroups((prev) => prev.filter((v) => v !== viewMoreValue))
    }
  }, [viewMoreOpen, viewMoreValue])

  const handleValueChange = (value: string[]) => {
    setExpandedGroups(value)
    const isViewMoreOpen = value.includes(viewMoreValue)
    if (isViewMoreOpen !== viewMoreOpen) {
      onViewMore()
    }
  }

  return (
    <>
      <div className="flex flex-col gap-2">
        {isLoading && (
          <div
            className={cn(
              "grid grid-cols-1 xl:grid-cols-2 gap-2",
              isSingleItem && "grid-cols-1 xl:grid-cols-1",
            )}
          >
            {Array.from({ length: MAX_VISIBLE_GROUPS }).map((_, i) => (
              <SignalCard isPending={true} key={`loading-${i}`} />
            ))}
          </div>
        )}
        {!isLoading && (
          <div className="flex flex-col gap-2">
            {noData && (
              <div className="flex flex-col items-center gap-2 pt-4 pb-10 text-center">
                <Signal className="size-8 text-chart-primary" />
                <div className="flex flex-col items-center">
                  <h3 className="font-medium text-lg">
                    {t("dashboard.signals.list.noSignals.title")}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t("dashboard.signals.list.noSignals.description")}
                  </p>
                </div>
              </div>
            )}
            {!noData && (
              <AccordionPrimitive.Root
                type="multiple"
                value={expandedGroups}
                onValueChange={handleValueChange}
                className={cn(
                  "grid grid-cols-1 xl:grid-cols-2 gap-2",
                  isSingleItem && "grid-cols-1 xl:grid-cols-1",
                )}
              >
                {initialGroups.map((group) => {
                  const value = `${group.groupKey}-${group.impact}`
                  return <SignalGroupItem key={value} group={group} value={value} />
                })}
                {showViewMore && (
                  <AccordionPrimitive.Item
                    value={viewMoreValue}
                    className="border-none overflow-hidden col-span-full"
                  >
                    <AccordionPrimitive.Header className="flex">
                      <AccordionPrimitive.Trigger
                        value={viewMoreValue}
                        className="flex flex-row items-center justify-center w-full"
                      >
                        <div className="flex flex-row items-center justify-center">
                          <Button variant="ghost" size="sm" onClick={onViewMore}>
                            <span>
                              {viewMoreOpen
                                ? t("dashboard.signals.list.viewLess")
                                : t("dashboard.signals.list.viewMore", {
                                    count: groups.length - sliceCount,
                                  })}
                            </span>
                            <ArrowDown
                              className={cn(
                                "text-foreground transition-transform duration-200 size-4",
                                viewMoreOpen && " rotate-180",
                              )}
                            />
                          </Button>
                        </div>
                      </AccordionPrimitive.Trigger>
                    </AccordionPrimitive.Header>
                    {additionalGroups.length > 0 && (
                      <AccordionPrimitive.Content className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down data-[state=open]:overflow-visible data-[state=closed]:overflow-hidden overflow-hidden col-span-full">
                        <div
                          className={cn(
                            "grid gap-2 pt-0 pb-0",
                            additionalGroups.length === 1
                              ? "grid-cols-1"
                              : "grid-cols-1 xl:grid-cols-2",
                          )}
                        >
                          {additionalGroups.map((group) => {
                            const value = `${group.groupKey}-${group.impact}`
                            return <SignalGroupItem key={value} group={group} value={value} />
                          })}
                        </div>
                      </AccordionPrimitive.Content>
                    )}
                  </AccordionPrimitive.Item>
                )}
              </AccordionPrimitive.Root>
            )}
          </div>
        )}
      </div>
    </>
  )
}
